/**
 * AnimalPassword Utilities
 *
 * Functions for generating and validating animal password codes.
 */

import { ANIMALS, ANIMAL_CODE_LENGTH, type AnimalType } from './types';

/**
 * Generate a random animal code of specified length
 * Uses Fisher-Yates shuffle for true randomness
 */
export function generateAnimalCode(length: number = ANIMAL_CODE_LENGTH): AnimalType[] {
  // Create a copy of the animals array (just the types)
  const animalTypes = ANIMALS.map((a) => a.type);

  // Fisher-Yates shuffle
  for (let i = animalTypes.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [animalTypes[i], animalTypes[j]] = [animalTypes[j], animalTypes[i]];
  }

  // Return first N animals
  return animalTypes.slice(0, length);
}

/**
 * Validate a student's animal code attempt against the correct code
 */
export function validateAnimalCode(
  attempt: (AnimalType | null)[],
  correct: AnimalType[]
): boolean {
  // Must have all boxes filled
  if (attempt.length !== correct.length) return false;
  if (attempt.some((a) => a === null)) return false;

  // Check each position matches
  return attempt.every((animal, index) => animal === correct[index]);
}

/**
 * Check how many animals are correctly placed
 */
export function countCorrectAnimals(
  attempt: (AnimalType | null)[],
  correct: AnimalType[]
): number {
  return attempt.reduce((count, animal, index) => {
    if (animal === correct[index]) return count + 1;
    return count;
  }, 0);
}

/**
 * Generate a unique device ID for session tracking
 */
export function generateDeviceId(): string {
  // Use crypto API if available, fallback to Math.random
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback: generate a random string
  return `device-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Check if a session has expired
 */
export function isSessionExpired(expiryTimestamp: number | null): boolean {
  if (!expiryTimestamp) return true;
  return Date.now() > expiryTimestamp;
}

/**
 * Calculate session expiry timestamp (24 hours from now)
 */
export function calculateSessionExpiry(): number {
  return Date.now() + 24 * 60 * 60 * 1000;
}

/**
 * Format animal code for display (e.g., for debugging)
 */
export function formatAnimalCode(code: AnimalType[]): string {
  return code.join(' → ');
}
